import { registerLocaleData } from '@angular/common';
import {
  getBrowserLang,
  loadTranslations,
  parseTranslations,
} from '@locl/core';

/**
 * Base url of the localization API route serving the published language packs
 */
const translationsPath = `/api/v2/localization/translations/pack/`;

/**
 * default locale picked as default.
 */
const defaultLocale = 'en';

/**
 * Loads translations from remote
 */
export async function lazyLoadTranslations() {
  const locale = getCurrentLanguage();
  let data;

  try {
    data = await fetchTranslationPack(locale);
  } catch (err) {
    data = await fetchTranslationPack(defaultLocale);
  }

  await initializeLocale(data.locale);
}

/**
 * Fetches and loads the language pack, rejecting on a non-OK response.
 * `fetchTranslations` from `@locl/core` ignores the HTTP status and only fails on a non-JSON body,
 * so the localization API's JSON ProblemDetails 404 would otherwise be loaded as an empty pack.
 */
async function fetchTranslationPack(locale: string) {
  const response = await fetch(`${translationsPath}${locale}.json`);

  if (!response.ok) {
    throw new Error(
      `Translation pack "${locale}" failed with status ${response.status}`,
    );
  }

  const bundle = parseTranslations(await response.text());
  loadTranslations(bundle);

  return bundle;
}

/**
 * Retrieve current language from storage
 */
export function getCurrentLanguage(): string {
  let currentLanguage =
    localStorage.getItem('phoenix-language') ||
    localStorage.getItem('language');

  if (!currentLanguage) {
    currentLanguage = getBrowserLang();
  }
  if (!currentLanguage) {
    currentLanguage = defaultLocale;
  }

  const split = currentLanguage.split('-');

  if (split.length === 1) {
    return currentLanguage;
  }

  return split
    .map((item, index) => {
      if (index === split.length - 1) {
        return item.toUpperCase();
      }

      return item;
    })
    .join('-');
}

/**
 * Load the appropriate locale module for pipe localization
 */
export function initializeLocale(localeId: string): Promise<void> {
  return import(
    /* webpackMode: "lazy" */
    `node_modules/@angular/common/locales/${localeId}.js`
  )
    .catch(() => {
      return import(
        /* webpackMode: "lazy" */
        `node_modules/@angular/common/locales/${defaultLocale}.js`
      );
    })
    .then((localeModule) => registerLocaleData(localeModule.default));
}

// As localize does not support ICU on component level
// based on https://github.com/angular/angular/issues/35912#issuecomment-595867379
export function plural<T extends number | string>(
  value: T,
  options: { [key in T]: string },
  fallback: string,
) {
  // Handle 0, 1, ... cases
  const directResult = options[value];
  if (directResult !== undefined) {
    return directResult;
  }
  // handle zero, one, two, few, many
  // ...
  return fallback;
}
